// TODO: Unificar tabela de banners no banco de dados
// - Migrar dados antigos (type: desktop/mobile) para novo formato (image + mobile_image)
// - Remover coluna 'type' da tabela banners
// - Garantir que todos os registros tenham image e/ou mobile_image
// - Atualizar queries do backend para usar novo formato

import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/Label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/Select";
import { Textarea } from "../components/ui/Textarea";
import { Switch } from "../components/ui/Switch";
import { DatePicker } from "../components/ui/DatePicker";
import { ArrowLeft, Save, Image as ImageIcon, Upload, Link as LinkIcon, Loader2, X } from "lucide-react";
import { bannerService, Banner } from "../services/banners";
import { toast } from "sonner";
import { formatToDateTimeLocal, getImageUrl, toUtcISOString } from "../utils";

interface NovoBannerFormProps {
  onBack: () => void;
  bannerId?: number | string;
}

export function NovoBannerForm({ onBack, bannerId: propBannerId }: NovoBannerFormProps) {
  const { id } = useParams();
  const bannerId = propBannerId || id;
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState<Partial<Banner>>({
    description: "",
    type: "desktop",
    active: true,
    starts_at: new Date().toISOString().slice(0, 16),
    expires_at: "",
    url: "",
    cta_text: "",
    link_target: "mesma-aba",
    utm_source: "",
    utm_campaign: "",
    image: "",
  });

  useEffect(() => {
    if (bannerId) {
      const fetchBannerData = async () => {
        setLoading(true);
        try {
          const data = await bannerService.getBannerById(bannerId);
          setFormData({
            ...data,
            starts_at: formatToDateTimeLocal(data.starts_at),
            expires_at: formatToDateTimeLocal(data.expires_at),
          });
        } catch (error) {
          console.error("Erro ao carregar dados do banner:", error);
          toast.error("Não foi possível carregar os dados para edição.");
        } finally {
          setLoading(false);
        }
      };
      fetchBannerData();
    }
  }, [bannerId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id.replace(/-/g, '_')]: value }));
  };

  const handleSelectChange = (id: string, value: string) => {
    setFormData(prev => ({ ...prev, [id.replace(/-/g, '_')]: value }));
  };

  const handleSwitchChange = (id: string, checked: boolean) => {
    setFormData(prev => ({ ...prev, [id]: checked }));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);

    try {
      const response = await bannerService.uploadImage(file);
      setFormData(prev => ({
        ...prev,
        image: response.path || response.location
      }));
      toast.success("Imagem enviada com sucesso!");
    } catch (error) {
      console.error("Erro no upload:", error);
      toast.error("Falha ao enviar a imagem.");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.image) {
      toast.error("A imagem é obrigatória.");
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        ...formData,
        starts_at: toUtcISOString(formData.starts_at) || formData.starts_at,
        expires_at: toUtcISOString(formData.expires_at) || formData.expires_at || undefined,
      };

      if (bannerId) {
        await bannerService.updateBanner(bannerId, dataToSend);
        toast.success("Banner atualizado com sucesso!");
      } else {
        await bannerService.createBanner(dataToSend);
        toast.success("Banner criado com sucesso!");
      }
      onBack();
    } catch (error) {
      console.error("Erro ao salvar banner:", error);
      toast.error("Erro ao salvar as informações do banner.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="flex-1 p-6 bg-gray-50 overflow-auto">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para Banners
        </Button>
        <h1 className="text-2xl font-bold">{bannerId ? 'Editar Banner' : 'Novo Banner'}</h1>
        <p className="text-gray-600">
          {bannerId ? 'Atualizar os dados da campanha' : 'Criar nova campanha visual'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                Informações Básicas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="type">Tipo do Banner</Label>
                <Select
                  value={formData.type}
                  onValueChange={(v) => handleSelectChange('type', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="desktop">Desktop (Computadores)</SelectItem>
                    <SelectItem value="mobile">Mobile (Celulares)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Descrição detalhada da campanha..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Configurações de Exibição</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="starts-at">Data de Início</Label>
                  <div className="mt-1">
                    <DatePicker
                      value={formData.starts_at}
                      onChange={(v) => setFormData(prev => ({ ...prev, starts_at: v }))}
                      placeholder="Data de início"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="expires-at">Data de Fim</Label>
                  <div className="mt-1">
                    <DatePicker
                      value={formData.expires_at}
                      onChange={(v) => setFormData(prev => ({ ...prev, expires_at: v }))}
                      placeholder="Data de fim"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="active"
                    checked={formData.active}
                    onCheckedChange={(checked) => handleSwitchChange('active', checked)}
                  />
                  <Label htmlFor="active">Banner ativo</Label>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Imagens e Mídia
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Imagem</Label>
                {formData.image ? (
                  <div className="relative mt-2">
                    <img src={getImageUrl(formData.image)} alt="Preview" className="w-full h-32 object-cover rounded-lg border" />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, image: "" }))}
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 shadow-md"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center mt-2 group hover:border-blue-400 transition-colors">
                    {uploading ? (
                      <Loader2 className="h-8 w-8 text-blue-500 mx-auto animate-spin" />
                    ) : (
                      <>
                        <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2 group-hover:text-blue-500 transition-colors" />
                        <p className="text-sm text-gray-600">1920x600px recomendado para desktop, 750x400px para mobile</p>
                        <Label htmlFor="image-upload" className="mt-2 block cursor-pointer">
                          <span className="bg-white border px-3 py-1.5 rounded-md text-sm font-medium hover:bg-gray-50 inline-block">
                            Selecionar Imagem
                          </span>
                        </Label>
                        <input
                          id="image-upload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleFileUpload}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LinkIcon className="h-5 w-5" />
                Link e Call-to-Action
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="url">URL de Destino</Label>
                <Input
                  id="url"
                  type="text"
                  value={formData.url}
                  onChange={handleInputChange}
                  placeholder="/bolsas"
                />
                <p className="text-xs text-gray-500 mt-1">Use caminhos relativos (ex: /bolsas)</p>
              </div>

              <div>
                <Label htmlFor="cta-text">Texto do Call-to-Action</Label>
                <Input
                  id="cta-text"
                  value={formData.cta_text}
                  onChange={handleInputChange}
                  placeholder="Ver Promoção, Compre Agora, Saiba Mais..."
                />
              </div>

              <div>
                <Label htmlFor="link-target">Comportamento do Link</Label>
                <Select
                  value={formData.link_target}
                  onValueChange={(v) => handleSelectChange('link-target', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o comportamento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mesma-aba">Mesma aba</SelectItem>
                    <SelectItem value="nova-aba">Nova aba</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="utm-source">UTM Source (Rastreamento)</Label>
                <Input
                  id="utm-source"
                  value={formData.utm_source}
                  onChange={handleInputChange}
                  placeholder="banner_homepage"
                />
              </div>

              <div>
                <Label htmlFor="utm-campaign">UTM Campaign</Label>
                <Input
                  id="utm-campaign"
                  value={formData.utm_campaign}
                  onChange={handleInputChange}
                  placeholder="verao_2024"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex gap-4 pt-6">
          <Button type="submit" className="flex-1" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {bannerId ? 'Atualizar Banner' : 'Criar Banner'}
              </>
            )}
          </Button>
          <Button type="button" variant="outline" onClick={onBack} disabled={loading}>
            Cancelar
          </Button>
        </div>
      </form>
    </main>
  );
}

export default NovoBannerForm;
